import { configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';

import liveSlice from './slices/liveSlice';
import taxRateSlice from './slices/taxRate';

import confirmSlice from '../components/modal/confirmSlice';
import modalCreateItemSlice from '../components/modal/createItem/slice';
import downloadSlice from '../components/download/downloadSlice';
import createGenericSlice from '../components/table/withSlice/tableSlice';
import imagePreviewSlice from '../components/imagePreview/imagePreviewSlice';


import alertsSlice from '../pages/alerts/slice';
import auditTrailsSlice from '../pages/auditTrails/slice';
import remindersSlice from '../pages/reminders/slice';

import menuSlice from '../layouts/menu/slice';
import customerLayoutSlice from '../layouts/customer/slice';
import manageLayoutSlice from '../layouts/manage/slice';

import customersSlice from '../pages/customers/customer/slice';
import customersBulkEditSlice from '../pages/customers/customer/bulkEdit/slice';
import customersCreditLimitSlice from '../pages/customers/customer/creditLimit/slice';
import customersOverviewSlice from '../pages/customers/customer/overview/slice';
import customersSiteSlice from '../pages/customers/customerSite/slice';
import customersSiteBulkEditSlice from '../pages/customers/customerSite/bulkEdit/slice';
import customersSiteChangeLocationSlice from '../pages/customers/customerSite/changeLocation/slice';
import customersSiteEmailTemplateSlice from '../pages/customers/customerSite/emailTemplate/slice';
import customersSiteCreateNewEmailSlice from '../pages/customers/customerSite/createNewEmail/slice';

import jobFormSlice from '../pages/jobs/slice';
import jobFormJobTypeHistorySlice from '../pages/jobs/form/jobTypeHistory/slice';
import jobFormJobSummarySlice from '../pages/jobs/form/jobSummary/slice';
import jobFormDriverOptionsSlice from '../pages/jobs/form/driverOptions/slice';
import jobFormBinDetailsServicesSlice from '../pages/jobs/form/services/slice';
import jobFormPrintPreviewSlice from '../pages/jobs/form/printPreview/slice';
import jobFormProformaInvoiceSlice from '../pages/jobs/form/proformaInvoice/slice';
import jobFormRecurringJobSlice from '../pages/jobs/form/recurringJob/slice';
import jobFormBillingJobSlice from '../pages/jobs/form/billingJob/slice';
import jobFormBillingJobSaveServiceSlice from '../pages/jobs/form/billingJob/saveService/slice';
import jobFormWeightChitSlice from '../pages/jobs/form/weightChit/slice';
import jobFormCustomerMiniSliceSlice from '../pages/jobs/form/customer/slice';
import jobFormCustomerSiteMiniSliceSlice from '../pages/jobs/form/customerSite/slice';
import jobFormDefaultLocationFormSlice from '../pages/jobs/form/defaultLocation/slice';
import jobFormAuditTrailSlice from '../pages/jobs/form/auditTrail/slice';
import jobFormDeleteSlice from '../pages/jobs/form/delete/slice';

import servicesSlice from '../pages/services/slice';
import servicesBillingCategorySlice from '../pages/services/billingCategory/slice';

import manageJobTypesSlice from '../pages/manage/jobs/jobTypes/slice';
import defaultLocationFormSlice from '../pages/manage/jobs/jobTypes/form/defaultLocation/slice';


export const Tables = {
  Customers: 'tableCustomers',
  CustomersSite: 'tableCustomersSite',
  CustomersEmail: 'tableCustomersEmail',
  Alerts: 'tableAlerts',
  AuditTrails: 'tableAuditTrails',
  Reminders: 'tableReminders',
  JobTypes: 'tableJobTypes',
  JobHistoryService: 'tableJobHistoryService',
}


export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;


const store = configureStore({
  reducer: {
    liveSlice: liveSlice.reducer,
    taxRateSlice: taxRateSlice.reducer,

    confirmSlice: confirmSlice.reducer,
    modalCreateItemSlice: modalCreateItemSlice.reducer,
    downloadSlice: downloadSlice.reducer,
    imagePreviewSlice: imagePreviewSlice.reducer,

    tableAlerts: createGenericSlice<any>(Tables.Alerts).reducer,
    tableAuditTrails: createGenericSlice<any>(Tables.AuditTrails).reducer,
    tableReminders: createGenericSlice<any>(Tables.Reminders).reducer,
    tableCustomers: createGenericSlice<any>(Tables.Customers).reducer,
    tableCustomersSite: createGenericSlice<any>(Tables.CustomersSite).reducer,
    tableCustomersEmail: createGenericSlice<any>(Tables.CustomersEmail).reducer,
    tableJobTypes: createGenericSlice<any>(Tables.JobTypes).reducer,
    tableJobHistoryService: createGenericSlice<any>(Tables.JobHistoryService).reducer,
    
    alertsSlice: alertsSlice.reducer,
    auditTrailsSlice: auditTrailsSlice.reducer,
    remindersSlice: remindersSlice.reducer,
  
    menuSlice: menuSlice.reducer,
    customerLayoutSlice: customerLayoutSlice.reducer,
    manageLayoutSlice: manageLayoutSlice.reducer,
    
    customersSlice: customersSlice.reducer,
    customersBulkEditSlice: customersBulkEditSlice.reducer,
    customersCreditLimitSlice: customersCreditLimitSlice.reducer,
    customersOverviewSlice: customersOverviewSlice.reducer,
    customersSiteSlice: customersSiteSlice.reducer,
    customersSiteBulkEditSlice: customersSiteBulkEditSlice.reducer,
    customersSiteChangeLocationSlice: customersSiteChangeLocationSlice.reducer,
    customersSiteEmailTemplateSlice: customersSiteEmailTemplateSlice.reducer,
    customersSiteCreateNewEmailSlice: customersSiteCreateNewEmailSlice.reducer,

    jobFormSlice: jobFormSlice.reducer,
    jobFormJobTypeHistorySlice: jobFormJobTypeHistorySlice.reducer,
    jobFormJobSummarySlice: jobFormJobSummarySlice.reducer,
    jobFormDriverOptionsSlice: jobFormDriverOptionsSlice.reducer,
    jobFormBinDetailsServicesSlice: jobFormBinDetailsServicesSlice.reducer,
    jobFormPrintPreviewSlice: jobFormPrintPreviewSlice.reducer,
    jobFormProformaInvoiceSlice: jobFormProformaInvoiceSlice.reducer,
    jobFormRecurringJobSlice: jobFormRecurringJobSlice.reducer,
    jobFormBillingJobSlice: jobFormBillingJobSlice.reducer,
    jobFormBillingJobSaveServiceSlice: jobFormBillingJobSaveServiceSlice.reducer,
    jobFormWeightChitSlice: jobFormWeightChitSlice.reducer,
    jobFormCustomerMiniSliceSlice: jobFormCustomerMiniSliceSlice.reducer,
    jobFormCustomerSiteMiniSliceSlice: jobFormCustomerSiteMiniSliceSlice.reducer,
    jobFormDefaultLocationFormSlice: jobFormDefaultLocationFormSlice.reducer,
    jobFormAuditTrailSlice: jobFormAuditTrailSlice.reducer,
    jobFormDeleteSlice: jobFormDeleteSlice.reducer,
    
    servicesSlice: servicesSlice.reducer,
    servicesBillingCategorySlice: servicesBillingCategorySlice.reducer,

    manageJobTypesSlice: manageJobTypesSlice.reducer,
    defaultLocationFormSlice: defaultLocationFormSlice.reducer,
  },
});

export { store };

export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useAppDispatch = () => useDispatch<AppDispatch>();
